import { useMemo, useState } from "react"
import { Skeleton, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { getEarliestBookableStart } from "../../../../../shared/components/createOrder/components/resource/resourceBookingCalendars/helpers"
import { CreateOrderDialogMode } from "../../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderType } from "../../../../../shared/components/createOrder/helpers"
import { useOrderDialogUrlState } from "../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { FormCard } from "../../../../../shared/components/forms/FormCard"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import { PrintPageOnlyStyles } from "../../../../../shared/components/print/PrintPageOnlyStyles"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { UserMeetingsReceipt } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { HostContactDetailsDialog } from "../../home/components/drawer/HostContactDetailsDialog"
import type { ServiceCenterBookingOrganizer } from "../../home/serviceCenterHomeTypes"

import { useExportMeetingRooms } from "../hooks/useExportMeetingRooms"
import { useMeetingRoomsOverview } from "../hooks/useMeetingRoomsOverview"
import { formatHourRange, formatTimeWindow, toSearchRegex } from "../meetingRoomsLib"
import { MeetingRoomStatusFilter, MeetingRoomsView } from "../meetingRoomsTypes"
import type {
    MeetingRoomBookingItem,
    MeetingRoomDrawerSelection,
    MeetingRoomRow,
    MeetingRoomTableRow,
} from "../meetingRoomsTypes"
import { MeetingRoomDrawer } from "./MeetingRoomDrawer"
import { MeetingRoomsHourGroups } from "./MeetingRoomsHourGroups"
import { MeetingRoomsPrintSummary } from "./MeetingRoomsPrintSummary"
import { MeetingRoomsRoomGroups } from "./MeetingRoomsRoomGroups"
import { MeetingRoomsTable } from "./MeetingRoomsTable"
import { MeetingRoomsTableFooter } from "./MeetingRoomsTableFooter"
import { MeetingRoomsToolbar } from "./MeetingRoomsToolbar"
import { OccupancyByHour } from "./OccupancyByHour"

const useStyles = makeStyles()((theme) => ({
    card: {
        "@media print": {
            "&&": {
                padding: 0,
                border: "none",
                background: "none",
                boxShadow: "none",
                // MUI Card clips its content, which drops whatever lands on the next page.
                overflow: "visible",
                printColorAdjust: "exact",
            },
        },
    },
    content: {
        width: "100%",
        "@media print": {
            rowGap: ".25rem",
        },
    },
    // Every printed page repeats this spacing, so a continued frame never touches the edges of the sheet.
    printPage: {
        "@media print": {
            display: "block",
            paddingBlock: "1.25rem",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
        },
    },
    printSheet: {
        "@media print": {
            display: "block",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.tailwind.slate[200]}`,
            borderRadius: "1rem",
            // Every printed page repeats the frame instead of showing one box cut in half; Chrome loses the
            // height of a flex box split this way, so the sheet is a plain block on paper.
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
        },
    },
    printSheetPadded: {
        "@media print": {
            padding: "1rem 1.5rem",
        },
    },
    subtitle: {
        marginTop: ".5rem",
        ...HIDDEN_IN_PRINT,
    },
}))

export function MeetingRoomsOverviewController() {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { openDialog } = useOrderDialogUrlState()
    const overview = useMeetingRoomsOverview()
    const { rooms, tableRows, hourWindow, date, view } = overview

    const [drawerSelection, setDrawerSelection] = useState<MeetingRoomDrawerSelection>()
    const [receiptOrderId, setReceiptOrderId] = useState<number>()
    const [host, setHost] = useState<ServiceCenterBookingOrganizer | null>(null)

    const hourLabel = formatTimeWindow(dateTimeFactory, hourWindow)
    const hourRangeLabel = formatHourRange(dateTimeFactory, hourWindow)
    const searchRegex = useMemo(() => toSearchRegex(overview.search), [overview.search])
    const exportMeetingRooms = useExportMeetingRooms({ rows: overview.exportRows, date, hourLabel: hourRangeLabel })
    const drawerRoom = drawerSelection && rooms.find((room) => room.id === drawerSelection.roomId)

    const windowStart = dateTimeFactory.fromMillis(hourWindow.startMillis)
    const earliestStart = getEarliestBookableStart(dateTimeFactory)
    const bookStart = windowStart < earliestStart ? earliestStart : windowStart
    const bookEnd = dateTimeFactory.fromMillis(hourWindow.endMillis)
    const handleBook = (room: MeetingRoomRow) =>
        openDialog({
            mode: CreateOrderDialogMode.CREATE,
            activeOrderTab: CreateOrderType.RESOURCE_BOOKING,
            bookingPrefill: {
                resourceId: room.id,
                start: dateTimeFactory.toApiISOMinutesFormat(bookStart),
                end: dateTimeFactory.toApiISOMinutesFormat(bookEnd),
            },
        })
    const openRow = ({ room, booking }: MeetingRoomTableRow) =>
        setDrawerSelection({ roomId: room.id, orderId: booking?.orderId })
    const openBookingItem = ({ room, booking }: MeetingRoomBookingItem) =>
        setDrawerSelection({ roomId: room.id, orderId: booking.orderId })
    const openRoom = (room: MeetingRoomRow) => setDrawerSelection({ roomId: room.id })

    return (
        <>
            <PrintPageOnlyStyles />
            <LocalizedStrict id="service-center-meeting-rooms-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_REPORTS_MEETING_ROOMS_ROUTE}
                    title="Meeting room occupancy"
                    subtitleElement={
                        <Typography variant="body2" color="text.secondary" className={classes.subtitle}>
                            {overview.roomsQuery.isInitialLoading ? (
                                <Skeleton width="14rem" />
                            ) : (
                                overview.summaryMessages.join(" · ")
                            )}
                        </Typography>
                    }
                />
            </LocalizedStrict>
            <FormCard className={classes.card}>
                <Stack spacing="1.5rem" className={classes.content}>
                    <MeetingRoomsPrintSummary
                        date={date}
                        view={view}
                        statusFilter={overview.statusFilter}
                        floor={overview.floor}
                        hourLabel={hourLabel}
                        shownRooms={tableRows.length}
                        totalRooms={rooms.length}
                        summaryMessages={overview.summaryMessages}
                    />
                    <MeetingRoomsToolbar
                        date={date}
                        view={view}
                        hourLabel={hourRangeLabel}
                        statusFilter={overview.statusFilter}
                        statusCounts={overview.statusCounts}
                        floor={overview.floor}
                        floorNames={overview.floorNames}
                        search={overview.search}
                        isLoading={overview.roomsQuery.isInitialLoading}
                        onDateChange={overview.setDate}
                        onViewChange={overview.setView}
                        onStatusFilterChange={overview.setStatusFilter}
                        onFloorChange={overview.setFloor}
                        onSearchChange={overview.setSearch}
                        onReset={overview.resetFilters}
                        onExport={exportMeetingRooms}
                    />

                    {view === MeetingRoomsView.STATUS && (
                        <>
                            <OccupancyByHour
                                hours={overview.hourOccupancy}
                                totalRooms={rooms.length}
                                isLoading={overview.roomsQuery.isInitialLoading}
                                selectedHour={overview.selectedHour}
                                currentHour={overview.currentHour}
                                onHourSelect={overview.filterBookedAtHour}
                                onPeakHourSelect={overview.filterBookedAtHour}
                            />
                            <Stack className={classes.printPage}>
                                <Stack className={classes.printSheet}>
                                    <MeetingRoomsTable
                                        rows={tableRows}
                                        pagination={overview.tablePagination}
                                        hasRooms={rooms.length > 0}
                                        isInitialLoading={overview.roomsQuery.isInitialLoading}
                                        isFetching={overview.roomsQuery.isFetching}
                                        isError={overview.roomsQuery.isError}
                                        onRowOpen={openRow}
                                        onHostOpen={setHost}
                                        onBook={bookStart < bookEnd ? handleBook : undefined}
                                        search={overview.search}
                                        hourLabel={hourLabel}
                                        hourRangeLabel={hourRangeLabel}
                                        dayEndMillis={overview.dayEndMillis}
                                    />
                                    <MeetingRoomsTableFooter
                                        shownRooms={tableRows.length}
                                        isNoMatch={!tableRows.length && rooms.length > 0}
                                        pagination={overview.tablePagination}
                                        contextLabel={`${hourLabel} · ${dateTimeFactory.formatWeekdayLongDate(date)}`}
                                        onPageChange={overview.setTablePageIndex}
                                        onPageSizeChange={overview.setTablePageSize}
                                    />
                                </Stack>
                            </Stack>
                        </>
                    )}
                    {view === MeetingRoomsView.BY_HOUR && (
                        <Stack className={classes.printPage}>
                            <Stack className={cx(classes.printSheet, classes.printSheetPadded)}>
                                <MeetingRoomsHourGroups
                                    isLoading={overview.roomsQuery.isInitialLoading}
                                    isFreeRoomsShown={overview.statusFilter === MeetingRoomStatusFilter.AVAILABLE}
                                    groups={overview.hourGroups}
                                    totalRooms={rooms.length}
                                    currentHour={overview.currentHour}
                                    searchRegex={searchRegex}
                                    onOpen={openBookingItem}
                                    onRoomOpen={openRoom}
                                />
                            </Stack>
                        </Stack>
                    )}
                    {view === MeetingRoomsView.BY_ROOM && (
                        <Stack className={classes.printPage}>
                            <Stack className={cx(classes.printSheet, classes.printSheetPadded)}>
                                <MeetingRoomsRoomGroups
                                    isLoading={overview.roomsQuery.isInitialLoading}
                                    groups={overview.roomGroups}
                                    searchRegex={searchRegex}
                                    onOpen={openBookingItem}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </FormCard>

            <MeetingRoomDrawer
                room={drawerRoom}
                selectedOrderId={drawerSelection?.orderId}
                nowMillis={overview.nowMillis}
                isToday={overview.isToday}
                hourWindow={hourWindow}
                hourLabel={hourLabel}
                dayStartMillis={overview.dayStartMillis}
                dayEndMillis={overview.dayEndMillis}
                onSelectBooking={openBookingItem}
                onReceiptOpen={setReceiptOrderId}
                onClose={() => setDrawerSelection(undefined)}
            />
            {receiptOrderId !== undefined && (
                <UserMeetingsReceipt open id={receiptOrderId} onClose={() => setReceiptOrderId(undefined)} />
            )}
            <HostContactDetailsDialog isOpen={host !== null} host={host} onClose={() => setHost(null)} />
        </>
    )
}
